import styled from 'styled-components';
import { Translate } from 'react-redux-i18n';

import { StyledButtonRemove } from './ButtonRemove/styles';
import { EDIT_PANEL_WIDTH } from './index';
import { getEditorScrollStyles } from '../styles';
import { StyleColor, NewStyleColor } from '../../../utils/styles-constants';
import { hexToRgba } from '../../../utils/style';

interface StyledEditPanelProps {
	isDisabled: boolean;
}

interface InputWrapperProps {
	hasButtonAdd: boolean;
	hasButtonRemove: boolean;
	isError?: boolean;
	hasLabel?: boolean;
	hasNoIcon?: boolean;
}

interface HeaderButtonProps {
	disabled?: boolean;
}

const StyledEditPanel = styled('div')`
	box-sizing: border-box;
	width: ${EDIT_PANEL_WIDTH}px;
	padding: 15px;
	padding-right: 0;
	border-radius: 13px;
	background-color: ${NewStyleColor.Grey900};
	box-shadow: 0 3px 35px 0 ${hexToRgba(NewStyleColor.Black, 50)};
	cursor: default;

	${({ isDisabled }: StyledEditPanelProps) => isDisabled && `
		pointer-events: none;

		&::after {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 1;
			background-color: ${hexToRgba(StyleColor.Black, 75)};
		}
	`}
`;

const StyledEditPanelInner = styled('div')`
	max-height: calc(100vh - 75px);
	padding-right: 15px;
	overflow-x: hidden;
	overflow-y: auto;

	${getEditorScrollStyles({ width: 4 })}
`;

const StyledIconMove = styled('div')`
	position: absolute;
	top: 16px;
	left: 7px;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	cursor: move;

	svg {
		width: 20px;
		height: 20px;
		fill: ${hexToRgba(NewStyleColor.White, 50)};
	}
`;

const Label = styled(Translate)`
	display: block;
	margin-bottom: 5px;
	padding-left: 10px;
	font-family: HelveticaNeue, sans-serif;
	font-size: 13px;
	line-height: 20px;
	letter-spacing: 1px;
	color: ${hexToRgba(NewStyleColor.White, 75)};
	text-transform: uppercase;
`;

const Button = styled('button')`
	box-sizing: border-box;
	display: flex;
	align-items: center;
	width: 100%;
	height: 44px;
	padding: 0 15px;
	border-radius: 6px;
	border: 0;
	outline: 0;
	font-family: HelveticaNeue, sans-serif;
	font-size: 17px;
	line-height: 44px;
	color: ${hexToRgba(NewStyleColor.White, 75)};
	background-color: ${hexToRgba(NewStyleColor.White, 10)};
	cursor: pointer;

	&:disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	&:hover {
		background-color: ${hexToRgba(NewStyleColor.White, 25)};
	}

	&:active {
		background-color: ${hexToRgba(NewStyleColor.White, 30)};
	}

	&:not(:last-of-type) {
		margin-bottom: 12px;
	}
`;

const ButtonBack = styled('button')`
	position: relative;
	height: 16px;
	padding: 0;
	padding-left: 20px;
	border: 0;
	outline: 0;
	font-family: HelveticaNeue, sans-serif;
	font-weight: 700;
	font-size: 15px;
	line-height: 16px;
	color: ${NewStyleColor.Blue450};
	background-color: transparent;
	cursor: pointer;

	svg {
		position: absolute;
		top: 3px;
		left: 0;
		width: 12px;
		height: 12px;
		fill: currentColor;
	}
`;

const InputWrapper = styled('div')`
	position: relative;
	margin: ${(props: InputWrapperProps) => props.hasLabel ? '5px' : '25px'} 0 20px 0;
	border-radius: 6px;
	background-color: ${hexToRgba(StyleColor.White, 10)};

	input {
		box-sizing: border-box;
		width: 100%;
		height: 40px;
		padding: 10px 70px 10px 50px;
		padding-right: ${(props: InputWrapperProps) => {
			if (props.hasButtonAdd && props.hasButtonRemove) {
				return '70px';
			} else if (props.hasButtonRemove) {
				return '40px';
			}
			return '10px';
		}};
		border: 1px solid ${(props: InputWrapperProps) => props.isError
			? StyleColor.LightRed
			: 'transparent'};
		outline: 0;
		border-radius: 6px;
		background-color: transparent;
		font-family: HelveticaNeue, sans-serif;
		font-size: 14px;
		line-height: 18px;
		color: ${StyleColor.White};
		-webkit-appearance: none;

		${({ hasNoIcon }: InputWrapperProps) => hasNoIcon && 'padding-left: 10px;'}

		&:hover:not(:disabled),
		&:focus:not(:disabled) {
			border: 1px solid ${(props: InputWrapperProps) => props.isError
				? `${StyleColor.LightRed}`
				: `${StyleColor.White}`};
		}

		&::placeholder {
			color: ${hexToRgba(StyleColor.White, 50)};
		};

		&::-ms-clear {
			display: none;
		}

		// override user agent autofill styles
		&:-webkit-autofill {
			-webkit-background-clip: text;
			-webkit-text-fill-color: ${StyleColor.White} !important;
		}
	}

	${StyledButtonRemove} {
		position: absolute;
		top: 9px;
		right: 9px;
	}
`;

const InputError = styled(Translate)`
	position: absolute;
	left: 0;
	bottom: 100%;
	font-family: HelveticaNeue, sans-serif;
	font-size: 14px;
	line-height: 20px;
	color: ${StyleColor.LightRed};
`;

const Header = styled('div')`
	position: relative;
	margin-bottom: 20px;
`;

const HeaderLabel = styled(Translate)`
	display: block;
	font-family: HelveticaNeue, sans-serif;
	font-weight: 500;
	font-size: 18px;
	line-height: 20px;
	color: ${hexToRgba(StyleColor.White, 75)};
	text-align: center;
`;

const HeaderButton = styled('button').attrs({
	type: 'button',
})`
	position: absolute;
	top: 0;
	padding: 0;
	border: 0;
	outline: 0;
	background-color: transparent;
	font-family: HelveticaNeue, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 20px;
	color: ${NewStyleColor.Blue450};
	letter-spacing: 0.5px;
	cursor: pointer;

	&:first-of-type {
		left: 0;
	}

	&:not(:first-of-type):last-of-type {
		right: 0;
	}

	${(props: HeaderButtonProps) => props.disabled && `
		pointer-events: none;
		opacity: 0.5;
	`}
`;

export {
	StyledEditPanel,
	StyledEditPanelInner,
	StyledIconMove,
	Label,
	Button,
	ButtonBack,
	InputWrapper,
	InputError,
	Header,
	HeaderLabel,
	HeaderButton,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/styles.ts